import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

/**
 * FEATURE: Assign Program to User
 * LOCATION: src/components/AssignProgramForm.jsx
 * DESCRIPTION: This component handles the logic for manually assigning a program to a specific user email via Supabase RPC.
 */
const AssignProgramForm = ({ onProgramAssigned }) => {
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(true);

  useEffect(() => {
    const fetchPrograms = async () => {
      setLoadingPrograms(true);
      try {
        const { data, error } = await supabase.from('programs').select('id, name');
        if (error) throw error;
        setPrograms(data || []);
      } catch (error) {
        toast.error("Error fetching programs", { description: "Could not load the list of programs." });
        console.error('Error fetching programs:', error);
      } finally {
        setLoadingPrograms(false);
      }
    };

    fetchPrograms();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !selectedProgram) {
      toast.error("Missing information", { description: "Please provide both an email and a program." });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.rpc('my_assign_program', {
        p_athlete_email: email,
        p_program_id: selectedProgram,
      });

      if (error) throw error;

      toast.success(`Program successfully assigned to ${email}.`);
      setEmail('');
      setSelectedProgram('');
      if (onProgramAssigned) {
        onProgramAssigned();
      }
    } catch (error) {
      toast.error("Assignment Failed", { description: error.message || "There was a problem assigning the program." });
      console.error('Error assigning program:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full" data-testid="assign-program-form">
      <CardHeader>
        <CardTitle>Assign Program to User</CardTitle>
        <CardDescription>Manually assign a program to a user by their email address.</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">User Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="program">Program</Label>
            <Select onValueChange={setSelectedProgram} value={selectedProgram} disabled={loadingPrograms}>
              <SelectTrigger id="program">
                <SelectValue placeholder={loadingPrograms ? "Loading programs..." : "Select a program"} />
              </SelectTrigger>
              <SelectContent>
                {programs.map((program) => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" disabled={loading || loadingPrograms}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Assign Program
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default AssignProgramForm;